import React from "react";
import {
  hasPermission,
  canView,
  canAdd,
  canUpdate,
  canDelete,
  canAddOrUpdate,
  hasPermissionByName,
  canViewByName,
  canAddByName,
  canUpdateByName,
  canDeleteByName,
} from "@utils/permissions";

interface PermissionGuardProps {
  /**
   * Numeric feature ID from backend (legacy usage).
   * Prefer using featureName for environments where IDs can vary.
   */
  featureId?: number;
  /**
   * Human‑readable feature name (e.g. "Billings", "User Management").
   * This is resilient across environments where feature IDs differ.
   */
  featureName?: string;
  action?: "view" | "add" | "update" | "delete" | "addOrUpdate";
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Component to conditionally render content based on permissions
 * 
 * @example
 * <PermissionGuard featureName="User Management" action="view">
 *   <UserManagementPage />
 * </PermissionGuard>
 * 
 * @example
 * <PermissionGuard 
 *   featureName="Billings"
 *   action="add" 
 *   fallback={<div>You don't have permission to add billings</div>}
 * >
 *   <AddBillingButton />
 * </PermissionGuard>
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  featureId,
  featureName,
  action = "view",
  fallback = null,
  children,
}) => {
  let hasAccess = false;

  if (featureName) {
    // Name‑based checks (preferred: independent of numeric IDs)
    switch (action) {
      case "view":
        hasAccess = canViewByName(featureName);
        break;
      case "add":
        hasAccess = canAddByName(featureName);
        break;
      case "update":
        hasAccess = canUpdateByName(featureName);
        break;
      case "delete":
        hasAccess = canDeleteByName(featureName);
        break;
      case "addOrUpdate":
        hasAccess =
          canAddByName(featureName) || canUpdateByName(featureName);
        break;
      default:
        hasAccess = hasPermissionByName(featureName, action);
    }
  } else if (typeof featureId === "number") {
    // Legacy ID‑based checks (kept for backward compatibility)
    switch (action) {
      case "view":
        hasAccess = canView(featureId);
        break;
      case "add":
        hasAccess = canAdd(featureId);
        break;
      case "update":
        hasAccess = canUpdate(featureId);
        break;
      case "delete":
        hasAccess = canDelete(featureId);
        break;
      case "addOrUpdate":
        hasAccess = canAddOrUpdate(featureId);
        break;
      default:
        hasAccess = hasPermission(featureId, action);
    }
  } else {
    // Neither featureId nor featureName provided – deny by default
    hasAccess = false;
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

export default PermissionGuard;

